import { getUnprocessableErrors } from '@10xlabs/utils'
import { useMutation } from '@apollo/client'
import { Formik, FormikHelpers } from 'formik'
import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams, useSearchParams } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import { IVehiclesV2FormStep2Values, IVehiclesV2FormValues } from 'features/Vehicles/types'
import { FeaturesForm } from 'features/Vehicles/components/FeaturesForm'
import { OdometerForm } from 'features/Vehicles/components/OdometerForm'
import { LicensePlatesForm } from 'features/Vehicles/components/LicensePlatesForm'
import { VehiclesV2FormUtils } from 'features/Vehicles/utils'
import {
  CHANGE_LICENSE_PLATES,
  CHANGE_ODOMETER_READING,
  CHANGE_VEHICLE_FEATURES
} from 'features/Vehicles/mutations-legacy'
import { GTChangeTVehicleOdometerReadingInput } from 'gql/types.generated'
import { getDifference } from 'utils/object'
import { schema } from './schema'

export const NewVehiclesV2Step2 = ({
  vehicle,
  onSubmit,
  onCancel
}: {
  vehicle?: IVehiclesV2FormValues
  onSubmit: () => void
  onCancel: () => void
}): ReactElement => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const params = useParams()
  const vehicleId = params.vehicleId || searchParams.get('vehicleId')
  const [changeVehicleFeatures, { error: changeVehicleFeaturesError }] = useMutation(CHANGE_VEHICLE_FEATURES)
  const [changeOdometerReading, { error: changeOdometerReadingError }] = useMutation(CHANGE_ODOMETER_READING)
  const [changeLicensePlates, { error: changeLicensePlatesError }] = useMutation(CHANGE_LICENSE_PLATES)
  const handleSubmit = async (
    values: IVehiclesV2FormStep2Values,
    { setSubmitting }: FormikHelpers<IVehiclesV2FormStep2Values>
  ): Promise<void> => {
    try {
      const oldFeatures = VehiclesV2FormUtils.mapVehicleFeaturesFormToMutation(vehicle as IVehiclesV2FormStep2Values)
      const newFeatures = VehiclesV2FormUtils.mapVehicleFeaturesFormToMutation(values)

      if (!isEmpty(getDifference(newFeatures, oldFeatures))) {
        await changeVehicleFeatures({
          variables: {
            id: vehicleId,
            input: newFeatures
          }
        })
      }

      if (values.odometerReading && values.odometerReading !== vehicle?.odometerReading) {
        const odometerInput: GTChangeTVehicleOdometerReadingInput = {
          odometerReading: values.odometerReading || 0
        }
        await changeOdometerReading({
          variables: {
            id: vehicleId,
            input: odometerInput
          }
        })
      }

      const oldLicensePlates = VehiclesV2FormUtils.mapVehicleLicensePlatesFormToMutation(vehicle)
      const newLicensePlates = VehiclesV2FormUtils.mapVehicleLicensePlatesFormToMutation(values)
      if (!isEmpty(getDifference(newLicensePlates, oldLicensePlates))) {
        await changeLicensePlates({
          variables: {
            id: vehicleId,
            input: newLicensePlates
          }
        })
      }
      onSubmit()
    } catch (ex) {
      // DO NOTHING
    }
    setSubmitting(false)
  }

  const initialValues: IVehiclesV2FormStep2Values = {
    features: {
      brandCoverage: vehicle?.features.brandCoverage || null,
      amenities: vehicle?.features.amenities || [],
      make: vehicle?.features.make || null,
      model: vehicle?.features.model || null,
      year: vehicle?.features.year || null
    },
    odometerReading: vehicle?.odometerReading || null,
    licensePlates: vehicle?.licensePlates || [
      {
        type: '',
        number: '',
        state: null
      }
    ]
  }

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues} validationSchema={schema(intl)}>
      {({ handleSubmit, isSubmitting }): ReactElement => {
        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <FormLayout.Card small>
              <FeaturesForm />
            </FormLayout.Card>
            <FormLayout.Card small>
              <OdometerForm />
            </FormLayout.Card>
            <FormLayout.Card small>
              <LicensePlatesForm />
            </FormLayout.Card>
            {changeVehicleFeaturesError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(changeVehicleFeaturesError)}>
                  <FormattedMessage
                    id="vehicles.form.features.error"
                    defaultMessage="There was an error while saving the features"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            {changeOdometerReadingError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(changeOdometerReadingError)}>
                  <FormattedMessage
                    id="vehicles.form.odometer.error"
                    defaultMessage="There was an error while saving the odometer reading"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            {changeLicensePlatesError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(changeLicensePlatesError)}>
                  <FormattedMessage
                    id="vehicles.form.licensePlates.error"
                    defaultMessage="There was an error while saving the license plates"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button type="button" kind="cancel" onClick={onCancel} disabled={isSubmitting}>
                <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="submit" kind="primary" loading={isSubmitting}>
                <FormattedMessage id="vehicles.form.step2.save" defaultMessage="Next" />
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
